import { Box, Card, Link, TableCell, TableRow, Typography } from "@mui/material";
import { ServerListData } from "../../modules/core/data/ListData";
import { HELP_CENTER_URL } from "../../modules/help/HelpCommon";
import * as React from "react";
import EmptyTable from "../../assets/empty_states/empty-generic.svg";
import { useIsDesktop } from "../../modules/layout/MainLayout";
import { Trans, useTranslation } from "react-i18next";

export interface EmptyTableConfig {
    icon?: React.ReactNode;
    iconPath?: string;
    title?: React.ReactNode;
    message?: React.ReactNode;
    actionButton?: React.ReactNode;
    hideHelpLink?: boolean;
}

export const useIsTableEmpty = (d: ServerListData<any, any>) => {
    return d.ready && d.data?.getItemsList()?.length > 0;
};

// ======================
// EmptyTableContent
// ======================

interface EmptyTableContentProps {
    emptyTableConfig?: EmptyTableConfig;
    isFilterActive?: boolean;
    cols?: number;
}

export const EmptyTableContent: React.FC<EmptyTableContentProps> = (p) => {
    const { emptyTableConfig, isFilterActive, cols } = p;
    const { t } = useTranslation();
    const title = isFilterActive ? t("common:table.noResults") : emptyTableConfig?.title || t("common:table.noData");
    const message = isFilterActive ? t("common:table.noResultsHelp") : emptyTableConfig?.message || null;
    const actionButton = isFilterActive ? null : emptyTableConfig?.actionButton;
    const iconSrc = isFilterActive ? EmptyTable : emptyTableConfig?.iconPath || EmptyTable;
    const isDesktop = useIsDesktop();

    const content = (
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <Box pt={6} pb={6} textAlign={"center"}>
                <Box pb={2}>{emptyTableConfig?.icon || <img src={iconSrc} alt={t("common:table.noResults")} height={"100px"} />}</Box>
                <Box pb={1}>
                    <Typography variant={"h6"}>{title}</Typography>
                </Box>
                <Box pb={2} pr={isDesktop ? 10 : 2} pl={isDesktop ? 10 : 2}>
                    <Typography variant={"subtitle2"}>{message}</Typography>
                </Box>
                {isFilterActive ? null : (
                    <>
                        {actionButton && (
                            <Box pb={2} pr={2} pl={2}>
                                {actionButton}
                            </Box>
                        )}
                        {!emptyTableConfig?.hideHelpLink && (
                            <Box pr={isDesktop ? 10 : 2} pl={isDesktop ? 10 : 2}>
                                <Typography color={"textSecondary"} variant={"caption"}>
                                    <Trans
                                        i18nKey="common:table.help"
                                        components={{
                                            customerCenter: <Link href={HELP_CENTER_URL} target={"_blank"} />,
                                        }}
                                    />
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );

    return cols ? (
        <TableRow>
            <TableCell colSpan={cols}>{content}</TableCell>
        </TableRow>
    ) : (
        <Card elevation={0}>{content}</Card>
    );
};
